import { Link, useParams } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import { useLang } from "@/i18n/LanguageContext";
import { MapPin, ArrowLeft, ShieldAlert, Building2, Stethoscope, BadgeCheck } from "lucide-react";

const UnknownHospital = () => {
    const { t } = useLang();
    return (
    <div className="pt-40 pb-32 text-center container-site">
        <p className="eyebrow text-moss">{t("hospital.unknown.eyebrow", "Hospital not found")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-5 text-forest">
            {t("hospital.unknown.title", "We couldn't find that hospital profile.")}
        </h1>
        <Link
            to="/hospitals"
            data-testid="hospital-unknown-back"
            className="btn-primary mt-10 inline-flex"
        >
            {t("hospital.unknown.back", "Back to Hospitals")}
        </Link>
    </div>
    );
};

export const HospitalDetail = () => {
    const { slug } = useParams();
    const { t, tHtml, site: siteData } = useLang();
    const { HOSPITALS } = siteData;
    const hosp = HOSPITALS.find((h) => h.slug === slug);
    usePageMeta(
        hosp ? `${hosp.name} ${t("hospital.metaSuffix", "— Partner Hospital")}` : t("hospital.metaTitle", "Partner Hospital"),
        t("hospital.metaDesc", "UniversHeal coordinates care with leading partner hospitals in Kerala, India — experienced specialists, international patient services and transparent coordination.")
    );

    if (!hosp) return <UnknownHospital />;

    return (
        <>
            <section className="pt-32 sm:pt-40 pb-10">
                <div className="container-site">
                    <Reveal>
                        <Link
                            to="/hospitals"
                            data-testid="hospital-back-link"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-moss hover:text-forest transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t("hospital.backAll", "All hospitals")}
                        </Link>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <div
                            data-testid="hospital-detail-demo-notice"
                            className="mt-6 flex items-start gap-3 bg-sage/70 border border-pine/15 rounded-xl px-5 py-4 max-w-3xl"
                        >
                            <ShieldAlert className="w-5 h-5 text-pine shrink-0 mt-0.5" />
                            <p className="text-xs sm:text-sm leading-relaxed text-charcoal/80">
                                {tHtml("hospital.demoNotice", <><strong>Demo profile.</strong> This is a placeholder profile awaiting verification. Details, imagery and accreditation status will be replaced with verified partner information before launch.</>)}
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                        <Reveal delay={0.1}>
                            <div className="rounded-2xl overflow-hidden h-72 sm:h-[460px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                                <img
                                    src={IMG[hosp.image]}
                                    alt={`Interior of ${hosp.name}, ${hosp.location}`}
                                    data-testid="hospital-detail-image"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </Reveal>
                        <div>
                            <Reveal delay={0.12}>
                                <p className="text-[11px] font-bold uppercase tracking-widest2 text-natural">
                                    {hosp.type}
                                </p>
                                <h1
                                    data-testid="hospital-detail-name"
                                    className="mt-3 font-serif font-medium text-4xl sm:text-5xl text-forest leading-tight"
                                >
                                    {hosp.name}
                                </h1>
                                <p className="mt-4 text-base text-moss flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    {hosp.location}
                                </p>
                            </Reveal>
                            <Reveal delay={0.16}>
                                <div className="mt-8">
                                    <p className="eyebrow text-moss mb-3">{t("hospital.specialtiesLabel", "Specialties")}</p>
                                    <div className="flex flex-wrap gap-2" data-testid="hospital-detail-specialties">
                                        {hosp.specialties.map((s) => (
                                            <span
                                                key={s}
                                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-pine bg-sage px-3.5 py-2 rounded-lg"
                                            >
                                                <Stethoscope className="w-3.5 h-3.5" />
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <div className="mt-8 flex items-start gap-3 bg-white border border-pine/15 rounded-xl px-5 py-4">
                                    <BadgeCheck className="w-5 h-5 text-moss shrink-0 mt-0.5" />
                                    <p
                                        data-testid="hospital-detail-accreditation"
                                        className="text-sm leading-relaxed text-charcoal/80"
                                    >
                                        {tHtml("hospital.accreditation", <>Accreditation: <strong>To be verified</strong> — details will be confirmed and displayed only after the partner verification process is complete. <span className="text-moss">[Placeholder — no accreditation is claimed.]</span></>)}
                                    </p>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section-pad pt-6">
                <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-20">
                    <div>
                        <Reveal>
                            <h2 className="font-serif font-medium text-3xl sm:text-4xl text-forest leading-tight">
                                {tHtml("hospital.aboutProvider", <>About this <span className="italic text-pine">provider</span></>)}
                            </h2>
                            <p
                                data-testid="hospital-detail-description"
                                className="mt-6 text-base leading-relaxed text-charcoal/85"
                            >
                                {hosp.desc}
                            </p>
                            <p className="mt-4 text-sm leading-relaxed text-moss">
                                {t("hospital.fullProfiles", "Full profiles — facilities, departments, international patient services and more — will be published as partners complete verification.")}
                            </p>
                        </Reveal>
                    </div>
                    <div>
                        <Reveal>
                            <h2 className="font-serif font-medium text-3xl sm:text-4xl text-forest leading-tight">
                                {tHtml("hospital.howWeWork", <>How we work with <span className="italic text-pine">providers</span></>)}
                            </h2>
                        </Reveal>
                        <div className="mt-6">
                            {[
                                t("hospital.how1", "We coordinate your enquiries, records and appointments"),
                                t("hospital.how2", "We help you understand options, requirements and information"),
                                t("hospital.how3", "We support you locally — travel, stay and communication"),
                                t("hospital.how4", "Treatment is delivered solely by the hospital's own medical team"),
                            ].map((item, i) => (
                                <Reveal key={item} delay={i * 0.05}>
                                    <div
                                        data-testid={`hospital-how-${i + 1}`}
                                        className="flex items-start gap-3 py-3.5 border-t border-pine/10"
                                    >
                                        <LeafMark className="w-4 h-4 mt-0.5 text-natural shrink-0" />
                                        <p className="text-[15px] text-charcoal/85">{item}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.1}>
                            <div className="mt-8 flex items-start gap-3 bg-forest text-ivory rounded-2xl px-5 py-4">
                                <Building2 className="w-5 h-5 text-natural shrink-0 mt-0.5" />
                                <p className="text-xs leading-relaxed text-ivory/75">
                                    {t("hospital.independence", "UniversHeal is independent of the hospitals it works with. It does not diagnose or treat patients, and treatment decisions rest entirely with you and your treating doctors.")}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <CtaBand
                headline={t("hospital.ctaHeadline", "Considering this provider for your care?")}
                text={t("hospital.ctaText", "Start with a medical opinion — we will coordinate the right specialist and pathway for your case.")}
            />
        </>
    );
};
